<template>
  <div class="flex flex-col">
    <RfDomainChooserWrapper ref="domainChooseDialog" @open="openLivePreview" />
    <RfSetupUsageTrackingModal
      v-if="state.showDialog"
      :showDialog="state.showDialog"
      :custom_field="custom_field"
      :isUpdating="state.isUpdating"
      :disabled="userStore.isDisabledWithMessage"
      @onSubmit="onSubmit"
      @resetForm="resetForm"
    />
    <v-dialog v-model="state.showImportListDialog" maxWidth="500">
      <v-card tag="form" @submit.prevent="onSubmitEventDataSource">
        <v-card-title class="headline">Add External Tracker</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="state.externalFieldsList"
            variant="outlined"
            density="compact"
            persistentPlaceholder
            label="Your imported data source"
            :items="fieldsByExternalTypes"
            itemTitle="label"
            returnObject
            placeholder="Start typing to select"
          >
            <template #item="{ props: autocompleteProps, item }">
              <v-list-item
                v-bind="autocompleteProps"
                :prependAvatar="logos[item.raw.connector_type]"
              >
                <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn
            class="cancel-btn"
            variant="outlined"
            style="width: 100px"
            @click="state.showImportListDialog = false"
            >Close</v-btn
          >
          <v-btn variant="flat" color="accent" type="submit">Submit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="state.showTrackingCodeDialog" maxWidth="700">
      <v-card>
        <v-card-title class="headline">Custom tracking code</v-card-title>
        <v-card-text>
          <RfSetupUsageTrackingCode :app="app" :customField="state.custom_field_code" />
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn
            class="cancel-btn"
            variant="outlined"
            style="width: 100px"
            @click="state.showTrackingCodeDialog = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <RfSimpleCard class="mb-5 px-10! py-10!">
      <div class="flex flex-col gap-5 text-sm">
        <p>
          Recurly Engage provides automated tracking of visits and minutes and allows you to
          customize additional tracking of various features within your app/site. Using a tag
          manager, add the following script to your app. If you're on Google Tag Manager go
          <a
            target="_blank"
            class="underline!"
            href="https://tagmanager.google.com/gallery/#/?filter=redfast"
            >here</a
          >
          for a one-click install. Please check our
          <a
            target="_blank"
            class="underline!"
            href="https://docs.recurly.com/recurly-engage/docs/add-the-redfast-tag"
            >documentation</a
          >
          for more details.
        </p>
        <span>
          <p v-if="state.currApp.flags?.sdk_active">
            Tracking is <b>Active <span class="promo-status-light green-color"></span></b>
          </p>
          <p v-else>
            Tracking <b>Not detected <span class="promo-status-light"></span></b>
          </p>
        </span>
        <RfInput
          v-model="code"
          disabled
          class="sdk-code"
          variant="outlined"
          hintMessage="Application unique identification code."
        >
          <template #append>
            <button
              type="button"
              class="relative inline-flex shrink-0 place-items-center before:absolute before:-inset-3 before:cursor-pointer"
              @click="copyToClipboard(code, 'Tag script copied to clipboard.')"
            >
              <RfCopyIcon class="h-6! w-6!" />
            </button>
          </template>
        </RfInput>
      </div>
    </RfSimpleCard>
    <RfSimpleCard class="rf-retention-form px-10! py-10!">
      <div class="flex flex-col gap-5">
        <div class="rf-settings-section-title"><h4>Customize Tracking</h4></div>
        <div class="text-sm">
          Provide details for the item you wish to track. Refer to the
          <a
            target="_blank"
            class="underline!"
            href="https://docs.recurly.com/recurly-engage/docs/usage-tracking-1"
            >documentation</a
          >
          for additional information.
        </div>

        <div class="mr-0 ml-auto">
          <div class="flex items-center" style="gap: 10px">
            <RfButtonNew
              type="secondary"
              size="x-small"
              :disabled="
                userStore.isDisabledWithMessage ||
                (!fieldsByExternalTypes.length && 'No external trackers available')
              "
              @click.stop="state.showImportListDialog = true"
            >
              <RfAddIcon class="h-5! w-5!" />
              Add External Tracker
            </RfButtonNew>
            <RfButtonNew
              type="secondary"
              size="x-small"
              :disabled="userStore.isDisabledWithMessage"
              @click.stop="state.showDialog = true"
            >
              <RfAddIcon class="h-5! w-5!" />
              Add New Tracker
            </RfButtonNew>
            <RfButtonNew
              type="secondary"
              size="x-small"
              :disabled="userStore.isDisabledWithMessage"
              @click.stop="() => domainChooseDialog?.open()"
            >
              <RfBoltIcon class="h-5! w-5!" />
              Add New Live Tracker
            </RfButtonNew>
          </div>
        </div>
        <v-table class="rf-settings-table-data all-rf-trackings">
          <template #default>
            <thead>
              <tr>
                <th class="text-left">Name</th>
                <th class="text-left">Label</th>
                <th class="text-left">Description</th>
                <th class="text-left">Type</th>
                <th class="text-left">Value</th>
                <th class="text-left">Prompts</th>
                <th></th>
              </tr>
            </thead>
            <tbody v-if="usageTrackingFields.length">
              <tr v-for="(custom, index) in usageTrackingFields" :key="index">
                <td class="rf-settings-table-col-one">
                  <div class="rf-name-wrapper">
                    {{ custom.name }}
                    <img
                      v-if="custom.connector_type !== 'redfast'"
                      :src="logos[custom.connector_type]"
                      width="80"
                    />
                  </div>
                </td>
                <td class="rf-settings-table-col-one">
                  {{ StringUtils.truncate(custom.label || "", 20) }}
                </td>
                <td class="rf-settings-table-col-two">
                  {{ StringUtils.truncate(custom.description || "", 30) }}
                </td>
                <td class="rf-settings-table-col-three">
                  {{ custom.data_type }}
                  <v-icon
                    v-if="custom.data_type === 'custom' && custom.id"
                    end
                    size="18"
                    @click="showTrackingCode(custom, index)"
                    >md:code</v-icon
                  >
                </td>
                <td class="rf-settings-table-col-four">
                  <RfTooltip
                    ref="tooltipEl"
                    :anchor="getAnchorEl(index)"
                    class="rf-row--configuration-tooltip mt-2 w-fit"
                  >
                    <template #tooltip>
                      {{ displayValues(custom) }}
                    </template>
                  </RfTooltip>
                  <div
                    :id="'tooltipAnchorEl_' + index"
                    :style="{ cursor: displayValues(custom).length > 30 ? 'pointer' : 'default' }"
                    class="pathgroup-zone-id-ctr"
                    @focus="handleFocus(displayValues(custom), index, tooltipEl)"
                    @mouseenter="handleFocus(displayValues(custom), index, tooltipEl)"
                    @blur="() => tooltipEl[index]?.hide && tooltipEl[index].hide()"
                    @mouseleave="() => tooltipEl[index]?.hide && tooltipEl[index].hide()"
                  >
                    {{
                      !!custom && typeof displayValues(custom) === "string"
                        ? StringUtils.truncate(displayValues(custom), 30)
                        : ""
                    }}
                  </div>
                </td>
                <td class="tracker-prompts-col">
                  <template v-if="allPromptsStore.getPromptsLoading">
                    <RfBaseSkeleton v-for="i in 2" :key="i" width="160" height="14" class="my-1" />
                  </template>
                  <ul v-else-if="custom.trackerPrompts.length">
                    <li
                      v-for="prompt in custom.trackerPrompts.slice(
                        0,
                        state.showPromptEntries[custom.label] ?? 3,
                      )"
                      :key="prompt.id"
                    >
                      <router-link :to="getPromptLink(prompt.id, $route.params.aid)">{{
                        prompt.name
                      }}</router-link>
                    </li>
                    <template v-if="custom.trackerPrompts.length > 3">
                      <template
                        v-if="
                          (state.showPromptEntries[custom.label] ?? 3) <
                          custom.trackerPrompts.length
                        "
                      >
                        <li>...</li>
                        <li>
                          <button
                            class="text-blue-50"
                            @click="
                              state.showPromptEntries[custom.label] = custom.trackerPrompts.length
                            "
                          >
                            Show more
                          </button>
                        </li>
                      </template>
                      <li v-else>
                        <button
                          class="text-blue-50"
                          @click="state.showPromptEntries[custom.label] = 3"
                        >
                          Show less
                        </button>
                      </li>
                    </template>
                  </ul>
                  <span v-else>N/A</span>
                </td>
                <td>
                  <div class="settings-button-wrapper">
                    <template
                      v-if="!['visits', 'minutes', 'concurrent_logins'].includes(custom.label)"
                    >
                      <RfButton
                        color="accent"
                        :icon="
                          !!userStore.isDisabledWithMessage
                            ? 'mdi:mdi-information-variant'
                            : 'md:edit'
                        "
                        variant="plain"
                        density="compact"
                        :disabled="!custom.id"
                        @click="onEdit(custom)"
                      ></RfButton>
                      <RfButton
                        color="error"
                        icon="md:clear"
                        variant="plain"
                        density="compact"
                        :disabled="userStore.isDisabledWithMessage"
                        @click="onDelete(custom)"
                      ></RfButton>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="6">
                  <div class="settings-no-data-message">Nothing to show.</div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-table>
      </div>
    </RfSimpleCard>
  </div>
</template>
<script setup>
import RfSimpleCard from "@/components/RfSimpleCard.vue";
import RfSetupUsageTrackingModal from "@/components/RfSettings/RfSetupUsageTrackingModal.vue";
import RfSetupUsageTrackingCode from "@/components/RfSettings/RfSetupUsageTrackingCode.vue";
import RfDomainChooserWrapper from "@/components/RfCommonCards/RfDomainChooserWrapper.vue";
import RfTooltip from "@/components/tooltip/RfTooltip.vue";
import { useLive } from "@/utils/composables/useLive";
import copyToClipboard from "@/utils/CopyToClipboard";
import RfButton from "@/components/buttons/RfButton.vue";
import StringUtils from "@/utils/StringUtils";
import { useToastsStore } from "@/pinia/toastsStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useUserStore } from "@/pinia/userStore";
import { useCustomFieldsStore } from "@/pinia/customFieldsStore";
import RfInput from "@/components/inputs/RfInput.vue";
import RfCopyIcon from "@/components/icons/RfCopyIcon.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfAddIcon from "@/components/icons/RfAddIcon.vue";
import RfBoltIcon from "@/components/icons/RfBoltIcon.vue";
import { getPromptLink } from "@/utils/getLink";
import { useAllPromptsStore } from "@/pinia/allPromptsStore";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import { useRoute } from "vue-router";
import { computed, reactive, onMounted, useTemplateRef } from "vue";

const logos = {
  segment: new URL("@/assets/images/logos/segment.svg", import.meta.url).href,
};

const props = defineProps({ app: { type: Object, required: true } });

const route = useRoute();
const toastsStore = useToastsStore();
const appsStore = useAppsStore();
const userStore = useUserStore();
const customFieldsStore = useCustomFieldsStore();
const allPromptsStore = useAllPromptsStore();
const live = useLive();
const tooltipEl = useTemplateRef("tooltipEl");
const domainChooseDialog = useTemplateRef("domainChooseDialog");

const custom_field = reactive({
  id: null,
  label: "",
  description: "",
  data_type: "",
  display_type: "",
  is_normalized: true,
  name: "",
  is_usage: true,
  values: [],
  options: {
    use_regex: false,
    custom_trigger_seconds: 2,
  },
});

const state = reactive({
  custom_field_code: {},
  isUpdating: false,
  showDialog: false,
  showImportListDialog: false,
  showTrackingCodeDialog: false,
  externalFieldsList: null,
  currApp: props.app,
  fieldType: "custom",
  showPromptEntries: {},
});

const code = computed(() => `<script src="${props.app.js_sdk}" async><\/script>`);

const custom_fields = computed(() => customFieldsStore.segmentCustomFields || []);

const hasAllData = computed(() => custom_field.label.length && custom_field.data_type.length);

const fields_by_connector_type = computed(() => {
  const values = custom_fields.value.reduce((total, item) => {
    const type = item.connector_type;
    if (!total[type]) total[type] = [];
    total[type].push(item);
    return total;
  }, {});
  return ["redfast", "segment", "aws_s3", "redfast_system", "ga", "mixpanel"].map(item => {
    return {
      label: item,
      values: values[item] || [],
    };
  });
});

const fields_by_visible_connector_type = computed(() => {
  return fields_by_connector_type.value.filter(
    item => ["segment", "redfast", "ga", "mixpanel"].indexOf(item.label) > -1,
  );
});

const usageTrackingFields = computed(() => {
  const fields = [];
  fields_by_visible_connector_type.value.forEach(item => {
    fields.push(...filterUsage(item.values));
  });
  return fields.map(field => ({
    ...field,
    trackerPrompts: allPromptsStore.prompts.filter(
      prompt => prompt.custom_filter?.[field.label] !== undefined,
    ),
  }));
});

const fieldsByExternalTypes = computed(() => {
  const values = custom_fields.value.reduce((total, item) => {
    const type = item.connector_type;
    if (!total[type]) total[type] = [];
    if (!item.is_usage) total[type].push(item);
    return total;
  }, {});
  const segment = values.segment || [];
  const ga = values.ga || [];
  const mixpanel = values.mixpanel || [];
  return [...segment, ...ga, ...mixpanel];
});

const filterUsage = fields => fields.filter(item => item.is_usage);

const handleFocus = (value, index, tooltipElements) => {
  if (value.length > 25) {
    tooltipElements[index]?.show && tooltipElements[index].show();
  }
};

const getAnchorEl = index => document.querySelector(`#tooltipAnchorEl_${index}`);

const openLivePreview = domain => {
  live.openLiveUrl(null, domain, { focus: "trackers" });
  domainChooseDialog.value?.close();
};

const resetForm = () => {
  state.isUpdating = false;
  custom_field.id = null;
  custom_field.label = "";
  custom_field.description = "";
  custom_field.data_type = "";
  custom_field.display_type = "";
  custom_field.is_normalized = true;
  custom_field.is_usage = true;
  custom_field.name = "";
  custom_field.values = [];
  custom_field.options = {
    use_regex: false,
    custom_trigger_seconds: 2,
  };
  state.externalFieldsList = null;
  state.showDialog = false;
};

const showTrackingCode = custom => {
  state.showTrackingCodeDialog = true;
  state.custom_field_code = custom;
};

const onSubmit = async args => {
  args && (custom_field.options.custom_trigger_args = args);
  if (custom_field.id) {
    await customFieldsStore.updateAppCustomField({
      appId: state.currApp.id,
      fieldId: custom_field.id,
      model: custom_field,
      fieldType: state.fieldType,
    });
    resetForm();
    state.showDialog = false;
  } else {
    const index = custom_fields.value.findIndex(item => {
      return item.label === custom_field.label;
    });
    if (hasAllData.value && index < 0) {
      customFieldsStore.createAppCustomField({
        model: { connector_type: "redfast", ...custom_field },
        appId: state.currApp.id,
        fieldType: state.fieldType,
      });
      resetForm();
      state.showDialog = false;
    } else {
      toastsStore.create({ type: "error", body: "Duplicate names or missing data" });
    }
  }
};

const onSubmitEventDataSource = async () => {
  if (!state.externalFieldsList) return;
  custom_field.label = state.externalFieldsList.label;
  custom_field.values = state.externalFieldsList.values;
  custom_field.description = state.externalFieldsList.description;
  custom_field.data_type = state.externalFieldsList.data_type;
  custom_field.is_normalized = true;
  custom_field.is_usage = true;
  const id = custom_fields.value.find(item => {
    return (
      ["segment", "ga", "mixpanel"].includes(item.connector_type) &&
      item.label === custom_field.label
    );
  })?.id;
  if (id) {
    await customFieldsStore.updateAppCustomField({
      appId: state.currApp.id,
      fieldId: id,
      model: { ...state.externalFieldsList, is_usage: true },
      fieldType: state.fieldType,
    });
  }
  resetForm();
  state.showImportListDialog = false;
};

const onDelete = async custom => {
  if (["segment", "ga", "mixpanel"].includes(custom.connector_type)) {
    await customFieldsStore.updateAppCustomField({
      appId: state.currApp.id,
      fieldId: custom.id,
      model: { ...custom, is_usage: false },
      fieldType: state.fieldType,
    });
  } else {
    customFieldsStore.deleteAppCustomField({
      appId: state.currApp.id,
      fieldId: custom.id,
      fieldType: state.fieldType,
    });
  }
};

const onEdit = custom => {
  state.showDialog = true;
  state.isUpdating = true;
  custom.values = custom.values || [];
  Object.assign(custom_field, custom);
};

const displayValues = customField => {
  if (customField.data_type === "page") {
    const settings = (customField.values || [])[0];
    if (!settings) return "";

    let display = settings.url_path || "";
    if (settings.query_params) {
      if (display) {
        display += "; ";
      }
      display += settings.query_params;
    }
    if (settings.url_hash) {
      if (display) {
        display += "; ";
      }
      display += settings.url_hash;
    }
    return display;
  }
  return customField.values && customField.values.length ? customField.values[0] : "";
};

onMounted(async () => {
  const appId = route.params.aid;
  if (!state.currApp.flags.sdk_active) appsStore.getSdkActiveFlags(appId);
  if (!custom_fields.value.length) {
    await customFieldsStore.getAppCustomFields({ appId, fieldType: state.fieldType });
  }
  if (!allPromptsStore.prompts.length) {
    allPromptsStore.getPrompts(appId, { nonBlocking: true });
  }
});
</script>

<style lang="scss" scoped>
:deep(.rf-row--configuration-tooltip .rf-tooltip) {
  max-width: 350px;
}

:deep(.tracker-prompts-col) {
  vertical-align: top;
  text-align: left;
  min-width: 220px;
}

.rf-name-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 5px;
}

.rf-name-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 5px;
}
</style>
